/**
 * Equinox Theme - Hybrid Load More & Smart Lazy-Loading Navigation
 * ASOS-style Load More pattern with Scroll Position Memory, Accessibility & SEO fallbacks.
 */

if (!customElements.get('collection-load-more')) {
  class CollectionLoadMore extends HTMLElement {
    constructor() {
      super();

      this.mode = this.dataset.mode || 'hybrid'; // 'hybrid' | 'infinite'
      this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
      this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
      this.pageSize = parseInt(this.dataset.pageSize, 10) || 24;
      this.totalProducts = parseInt(this.dataset.totalProducts, 10) || 0;
      this.sectionId = this.dataset.sectionId || '';
      this.baseUrl = this.dataset.baseUrl || window.location.href;

      this.isLoading = false;
      this.observer = null;
      this.autoLoadingActive = false;
      this.frictionHaltPageCount = 3; // Halts every 3 pages (72, 144, etc.) in hybrid mode

      // Bound methods
      this.handleIntersect = this.handleIntersect.bind(this);
      this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this);
      this.handleViewportResize = this.handleViewportResize.bind(this);
      this.handleProductClick = this.handleProductClick.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
    }

    connectedCallback() {
      this.initElements();
      if (!this.productGrid) return;

      this.initEventListeners();
      this.initScrollRestoration();
      this.setupModeBehavior();
      this.updateProgress(0, false);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.btn) {
        this.btn.removeEventListener('click', this.handleLoadMoreClick);
      }
      window.removeEventListener('resize', this.handleViewportResize);
      window.removeEventListener('popstate', this.handlePopState);
      if (this.productGrid) {
        this.productGrid.removeEventListener('click', this.handleProductClick);
      }
    }

    initElements() {
      this.section = this.closest('.shopify-section') || document;
      this.productGrid = this.section.querySelector('.product-grid');
      this.btn = this.querySelector('.collection-load-more__btn');
      this.countCurrentEl = this.querySelector('.collection-load-more__count-current');
      this.countTotalEl = this.querySelector('.collection-load-more__count-total');
      this.barFill = this.querySelector('.collection-load-more__bar-fill');
      this.sentinel = this.querySelector('.collection-load-more__sentinel');
      this.skeletonContainer = this.querySelector('.collection-load-more__skeleton-container');
      this.liveRegion = this.querySelector('.collection-load-more__live-region');
    }

    initEventListeners() {
      if (this.btn) {
        this.btn.addEventListener('click', this.handleLoadMoreClick);
      }

      window.addEventListener('resize', this.handleViewportResize, { passive: true });
      window.addEventListener('popstate', this.handlePopState);

      // Track clicked products for scroll restoration
      if (this.productGrid) {
        this.productGrid.addEventListener('click', this.handleProductClick);
      }
    }

    isMobileViewport() {
      return window.matchMedia('(max-width: 767.98px)').matches;
    }

    setupModeBehavior() {
      if (this.currentPage >= this.totalPages) {
        this.markAllLoaded();
        return;
      }

      if (this.mode === 'infinite') {
        // Continuous auto-loading on scroll
        this.autoLoadingActive = true;
        this.enableObserver();
        if (this.btn) this.btn.style.display = 'none';
      } else {
        // Hybrid mode (ASOS-style)
        if (this.isMobileViewport()) {
          // Mobile: direct Load More button, no auto lazy-loading
          this.autoLoadingActive = false;
          this.disableObserver();
          if (this.btn) this.btn.style.display = '';
        } else {
          // Desktop: auto-load unless at a friction halt threshold (multiples of 3)
          if (this.currentPage % this.frictionHaltPageCount === 0) {
            this.autoLoadingActive = false;
            this.disableObserver();
            if (this.btn) this.btn.style.display = '';
          } else {
            this.autoLoadingActive = true;
            this.enableObserver();
            if (this.btn) this.btn.style.display = 'none';
          }
        }
      }
    }

    enableObserver() {
      if (!this.sentinel || !('IntersectionObserver' in window)) return;
      if (this.observer) this.observer.disconnect();

      this.observer = new IntersectionObserver(this.handleIntersect, {
        root: null,
        rootMargin: '400px 0px 400px 0px', // Pre-fetch before user hits the bottom
        threshold: 0.01
      });

      this.observer.observe(this.sentinel);
    }

    disableObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    handleIntersect(entries) {
      const [entry] = entries;
      if (entry && entry.isIntersecting && this.autoLoadingActive && !this.isLoading) {
        if (this.currentPage < this.totalPages) {
          this.loadNextPage();
        }
      }
    }

    handleLoadMoreClick(evt) {
      if (evt) evt.preventDefault();
      if (this.isLoading || this.currentPage >= this.totalPages) return;

      if (this.mode === 'hybrid' && !this.isMobileViewport()) {
        // Desktop: clicking Load More resumes auto-loading for the next cycle
        this.autoLoadingActive = true;
      }

      this.loadNextPage();
    }

    handleViewportResize() {
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.setupModeBehavior();
      }, 150);
    }

    handleProductClick(evt) {
      const link = evt.target.closest('a');
      if (!link) return;

      const productCard = link.closest('.product-block');
      const productId = productCard ? productCard.dataset.productId : null;

      try {
        const scrollData = {
          pathname: window.location.pathname,
          search: window.location.search,
          page: this.currentPage,
          scrollY: window.scrollY,
          productId: productId,
          timestamp: Date.now()
        };
        sessionStorage.setItem('eq_collection_scroll_state', JSON.stringify(scrollData));
      } catch (e) {
        // Ignore storage errors
      }
    }

    initScrollRestoration() {
      // Check if URL has ?page=N or if sessionStorage has saved state
      const urlParams = new URLSearchParams(window.location.search);
      const urlPage = parseInt(urlParams.get('page'), 10);
      
      let savedState = null;
      try {
        const saved = sessionStorage.getItem('eq_collection_scroll_state');
        if (saved) {
          savedState = JSON.parse(saved);
          // Only use if within same path and less than 1 hour old
          if (savedState.pathname !== window.location.pathname || (Date.now() - savedState.timestamp) > 3600000) {
            savedState = null;
          }
        }
      } catch (e) {
        savedState = null;
      }

      const targetPage = urlPage || (savedState ? savedState.page : 1);
      const targetScrollY = savedState ? savedState.scrollY : null;

      if (targetPage > 1 && this.currentPage < targetPage) {
        this.preloadPagesUpTo(targetPage, targetScrollY);
      } else if (targetScrollY && Math.abs(window.scrollY - targetScrollY) > 50) {
        // Single page scroll restore
        requestAnimationFrame(() => {
          window.scrollTo({ top: targetScrollY, behavior: 'instant' });
        });
      }
    }

    async preloadPagesUpTo(targetPage, targetScrollY) {
      if (this.isLoading) return;
      this.isLoading = true;
      this.classList.add('collection-load-more--loading');

      const maxPage = Math.min(targetPage, this.totalPages);
      let page = this.currentPage + 1;

      while (page <= maxPage) {
        try {
          const success = await this.fetchAndAppendBatch(page, false);
          if (!success) break;
          page += 1;
        } catch (err) {
          console.error('[Equinox LoadMore] Preload error:', err);
          break;
        }
      }

      this.isLoading = false;
      this.classList.remove('collection-load-more--loading');
      this.setupModeBehavior();

      // Restore scroll coordinate
      if (targetScrollY) {
        setTimeout(() => {
          window.scrollTo({ top: targetScrollY, behavior: 'instant' });
        }, 100);
      }
    }

    handlePopState() {
      const urlParams = new URLSearchParams(window.location.search);
      const urlPage = parseInt(urlParams.get('page'), 10) || 1;
      if (urlPage > this.currentPage) {
        this.preloadPagesUpTo(urlPage, null);
      }
    }

    async loadNextPage() {
      if (this.isLoading || this.currentPage >= this.totalPages) return;
      const nextPage = this.currentPage + 1;

      this.isLoading = true;
      this.classList.add('collection-load-more--loading');
      if (this.btn) this.btn.setAttribute('aria-busy', 'true');

      const success = await this.fetchAndAppendBatch(nextPage, true);

      this.isLoading = false;
      this.classList.remove('collection-load-more--loading');
      if (this.btn) this.btn.removeAttribute('aria-busy');

      if (success) {
        this.setupModeBehavior();
      }
    }

    buildFetchUrl(pageNumber) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', pageNumber);
      if (this.sectionId) {
        url.searchParams.set('section_id', this.sectionId);
      }
      return url.toString();
    }

    async fetchAndAppendBatch(pageNumber, updateHistoryUrl = true) {
      const fetchUrl = this.buildFetchUrl(pageNumber);

      try {
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(`HTTP Error ${response.status}`);

        const htmlText = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');

        const newGrid = doc.querySelector('.product-grid');
        if (!newGrid) return false;

        const newItems = Array.from(newGrid.children);
        if (newItems.length === 0) {
          this.markAllLoaded();
          return false;
        }

        // Set lazy loading on all appended items below fold
        newItems.forEach((item) => {
          const imgs = item.querySelectorAll('img');
          imgs.forEach((img) => {
            if (!img.getAttribute('loading')) {
              img.setAttribute('loading', 'lazy');
            }
          });
          this.productGrid.appendChild(item);
        });

        this.currentPage = pageNumber;

        // Update URL query state without page reload
        if (updateHistoryUrl) {
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('page', pageNumber);
          window.history.replaceState({ page: pageNumber }, '', currentUrl.toString());
        }

        // Update ASOS progress tracking and accessibility announcements
        this.updateProgress(newItems.length, true);

        // Check if all pages are now loaded
        if (this.currentPage >= this.totalPages) {
          this.markAllLoaded();
        }

        return true;
      } catch (error) {
        console.error('[Equinox LoadMore] Error fetching batch:', error);
        return false;
      }
    }

    updateProgress(newBatchCount = 0, shouldAnnounce = false) {
      if (!this.productGrid) return;

      const loadedItems = this.productGrid.querySelectorAll('.product-block');
      const count = loadedItems.length || this.productGrid.children.length;
      const loadedCount = Math.min(count, this.totalProducts || count);
      const total = this.totalProducts || loadedCount;

      if (this.countCurrentEl) {
        this.countCurrentEl.textContent = loadedCount;
      }
      if (this.countTotalEl) {
        this.countTotalEl.textContent = total;
      }

      if (this.barFill && total > 0) {
        const percentage = Math.min(100, Math.round((loadedCount / total) * 100));
        this.barFill.style.width = `${percentage}%`;
        this.barFill.setAttribute('aria-valuenow', percentage);
      }

      // Screen reader announcement (WCAG 2.2 AA)
      if (shouldAnnounce && this.liveRegion && newBatchCount > 0) {
        const itemType = this.dataset.itemsLabel || 'products';
        const announcement = `Loaded ${newBatchCount} more ${itemType}. Showing ${loadedCount} of ${total} ${itemType}.`;
        this.liveRegion.textContent = '';
        // Small delay to ensure assistive technology detects textContent change
        setTimeout(() => {
          this.liveRegion.textContent = announcement;
        }, 50);
      }
    }

    markAllLoaded() {
      this.classList.add('collection-load-more--all-loaded');
      this.disableObserver();
      if (this.btn) {
        this.btn.style.display = 'none';
        this.btn.setAttribute('disabled', 'disabled');
      }
      this.updateProgress(0, false);
    }
  }

  customElements.define('collection-load-more', CollectionLoadMore);
}
